// CourseContent API methods — mixin applied to the ApiService prototype chain.
// Thin request wrappers for course/elective content CRUD and the recording
// upload lifecycle (presign / merge-upload / multipart initiate-complete-abort).
// The heavy XHR upload drivers live in ./recordingUploadApi. `this` binds to the
// ApiService instance, so this.request() and cross-domain calls resolve via the chain.
const withCourseContentApi = (Base) => class extends Base {
  // Course Content Management
  async getCourseContent(courseId, { includeProcessing = false } = {}) {
    const qs = includeProcessing ? '?includeProcessing=1' : '';
    return this.request(`/course-content/${courseId}${qs}`);
  }

  async addCourseContent(courseId, contentData) {
    // Check if contentData is FormData (for file uploads)
    const isFormData = contentData instanceof FormData;
    const onUploadProgress = isFormData ? contentData.__onUploadProgress : null;
    if (isFormData && onUploadProgress) {
      const signal = contentData.__signal; // set by the form so the upload can be cancelled
      delete contentData.__onUploadProgress;
      delete contentData.__signal;
      return this.requestWithUploadProgress(`/course-content/${courseId}`, {
        method: 'POST',
        body: contentData,
        onUploadProgress,
        signal
      });
    }
    
    return this.request(`/course-content/${courseId}`, {
      method: 'POST',
      timeout: isFormData || contentData?.s3Key ? null : undefined,
      headers: isFormData ? {} : {
        'Content-Type': 'application/json'
      },
      body: isFormData ? contentData : JSON.stringify(contentData)
    });
  }

  async getRecordingProcessingStatus(contentId) {
    return this.request(`/course-content/${contentId}/processing-status`);
  }

  async getRecordingMergeStatus(contentId) {
    return this.request(`/course-content/${contentId}/merge-status`);
  }

  async createCourseRecordingS3Upload(courseId, file, recordingDate, keyOverride, allowOverwrite = false, fileNameSuffix) {
    return this.request(`/course-content/${courseId}/recordings/s3-presign`, {
      method: 'POST',
      timeout: null,
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type || 'video/mp4',
        fileSize: file.size,
        ...(keyOverride ? { key: keyOverride } : { recordingDate, ...(fileNameSuffix ? { fileNameSuffix } : {}) }),
        ...(allowOverwrite ? { allowOverwrite: true } : {})
      })
    });
  }

  /**
   * Remove recording object(s) that were uploaded to the ingest bucket but then
   * cancelled (or interrupted by a dropped connection). Also aborts an open
   * multipart session when an uploadId is supplied. Best-effort: the server
   * scopes deletes to the course's recordings prefix.
   */
  async cleanupIngestRecording(courseId, { key, keys, uploadId } = {}) {
    return this.request(`/course-content/${courseId}/recordings/s3-cleanup`, {
      method: 'POST',
      body: JSON.stringify({ key, keys, uploadId })
    });
  }

  // ---------- Recording merge/HLS-transcode toggles ----------

  async getRecordingSettings() {
    return this.request(`/course-content/recording-settings`);
  }

  async updateRecordingSettings(payload) {
    // payload: { mergeEnabled?: boolean, hlsEnabled?: boolean }
    return this.request(`/course-content/recording-settings`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Puts a section's items in the given order. payload: { scopeType: 'course'|'elective', scopeId, type, ids }
  async reorderCourseContent(payload) {
    return this.request(`/course-content/reorder`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Where the signed-in person stopped in a recording: { success, positionSeconds }.
  async getRecordingProgress(contentId) {
    return this.request(`/course-content/${contentId}/progress`);
  }

  // payload: { positionSeconds, durationSeconds? }. A position of 0 clears it.
  async saveRecordingProgress(contentId, payload) {
    return this.request(`/course-content/${contentId}/progress`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // ---------- Site-wide "email students about new content" switch ----------

  async getContentNotificationSettings() {
    return this.request(`/course-content/notification-settings`);
  }

  async updateContentNotificationSettings(payload) {
    // payload: { emailEnabled: boolean } - admins only
    return this.request(`/course-content/notification-settings`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // ---------- Merge upload (multi-file → single merged recording) ----------

  async startMergeUpload(courseId, payload) {
    // payload: { title, recordingDate, week?, fileCount }
    return this.request(`/course-content/${courseId}/recordings/merge-upload/start`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async registerMergeUploadSource(courseId, payload) {
    // payload: { placeholderId, position, key, fileSize, originalFileName }
    return this.request(`/course-content/${courseId}/recordings/merge-upload/source-uploaded`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async abortMergeUpload(courseId, placeholderId, { deletePlaceholder = false } = {}) {
    return this.request(`/course-content/${courseId}/recordings/merge-upload/abort`, {
      method: 'POST',
      body: JSON.stringify({ placeholderId, ...(deletePlaceholder ? { deletePlaceholder: true } : {}) }),
    });
  }

  async mergeCourseRecordings(courseId, payload) {
    // payload: { recordingIds: number[], title: string, week?: number, recordingDate: string }
    return this.request(`/course-content/${courseId}/recordings/merge`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ---------- S3 Multipart Upload (for files > 5 GB) ----------

  async initiateMultipartUpload(courseId, file, recordingDate, keyOverride, allowOverwrite = false, fileNameSuffix) {
    return this.request(`/course-content/${courseId}/recordings/s3-multipart/initiate`, {
      method: 'POST',
      timeout: null,
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type || 'video/mp4',
        fileSize: file.size,
        ...(keyOverride ? { key: keyOverride } : { recordingDate, ...(fileNameSuffix ? { fileNameSuffix } : {}) }),
        ...(allowOverwrite ? { allowOverwrite: true } : {}),
      }),
    });
  }

  async completeMultipartUpload(courseId, key, uploadId, parts) {
    return this.request(`/course-content/${courseId}/recordings/s3-multipart/complete`, {
      method: 'POST',
      timeout: null,
      body: JSON.stringify({ key, uploadId, parts }),
    });
  }

  async abortMultipartUpload(courseId, key, uploadId) {
    return this.request(`/course-content/${courseId}/recordings/s3-multipart/abort`, {
      method: 'POST',
      body: JSON.stringify({ key, uploadId }),
    });
  }

  async updateCourseContent(contentId, contentData) {
    return this.request(`/course-content/${contentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(contentData)
    });
  }

  // The edit form saves its fields as JSON, which can't carry a file; a file chosen
  // while editing a reading, slide or syllabus goes here after that save.
  // No time limit, and onUploadProgress({ loaded, total, percent }) drives the progress bar.
  async replaceCourseContentFile(contentId, file, onUploadProgress, signal) {
    const body = new FormData();
    body.append('file', file);
    return this.requestWithUploadProgress(`/course-content/${contentId}/file`, {
      method: 'POST',
      body,
      onUploadProgress,
      signal
    });
  }

  async deleteCourseContent(contentId, { confirm } = {}) {
    return this.request(`/course-content/${contentId}`, {
      method: 'DELETE',
      ...(confirm ? { body: JSON.stringify({ confirm }) } : {})
    });
  }

  // List the ingest/serving bucket files associated with a recording so the
  // delete confirmation can show exactly what will be removed.
  async getRecordingDeleteFiles(contentId) {
    return this.request(`/course-content/${contentId}/recording-files`);
  }

  async getEffectivePermissions({ scopeType = 'course', scopeId } = {}) {
    const params = new URLSearchParams({ scopeType });
    if (scopeId !== undefined && scopeId !== null) params.set('scopeId', scopeId);
    return this.request(`/permissions/me?${params.toString()}`);
  }

  // Elective Content API methods
  async getElectiveContent(electiveId, { includeProcessing = false } = {}) {
    const qs = includeProcessing ? '?includeProcessing=1' : '';
    return this.request(`/course-content/elective/${electiveId}${qs}`);
  }

  async addElectiveContent(electiveId, contentData) {
    // Check if contentData is FormData (for file uploads)
    const isFormData = contentData instanceof FormData;
    const onUploadProgress = isFormData ? contentData.__onUploadProgress : null;
    if (isFormData && onUploadProgress) {
      const signal = contentData.__signal; // set by the form so the upload can be cancelled
      delete contentData.__onUploadProgress;
      delete contentData.__signal;
      return this.requestWithUploadProgress(`/course-content/elective/${electiveId}`, {
        method: 'POST',
        body: contentData,
        onUploadProgress,
        signal
      });
    }

    return this.request(`/course-content/elective/${electiveId}`, {
      method: 'POST',
      timeout: isFormData ? null : undefined,
      headers: isFormData ? {} : {
        'Content-Type': 'application/json'
      },
      body: isFormData ? contentData : JSON.stringify(contentData)
    });
  }

  // Note: updateCourseContent and deleteCourseContent work for both courses and electives

  async checkUserExists(email) {
    return this.publicRequest(`/users/check-exists`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });
  }
};

export default withCourseContentApi;
